import React from 'react';

/** Animated shimmer block — pass className to control size. */
export function Skeleton({ className = '' }: { className?: string }) {
  return (
    <div
      className={`animate-pulse rounded-lg bg-slate-700/50 ${className}`}
    />
  );
}

/** A fixture-card shaped skeleton row. */
export function FixtureCardSkeleton() {
  return (
    <div className="card p-4 sm:p-5">
      <div className="flex items-center gap-4">
        {/* Logo placeholders */}
        <div className="flex -space-x-2 shrink-0">
          <Skeleton className="w-10 h-10 rounded-full" />
          <Skeleton className="w-10 h-10 rounded-full" />
        </div>
        {/* Text placeholders */}
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-48 max-w-full" />
          <Skeleton className="h-3 w-32 max-w-full" />
        </div>
        {/* Price placeholder */}
        <div className="hidden sm:block space-y-2 text-right">
          <Skeleton className="h-5 w-20 ml-auto" />
          <Skeleton className="h-3 w-16 ml-auto" />
        </div>
      </div>
    </div>
  );
}

/** A grid of team-card skeletons. */
export function TeamCardSkeleton() {
  return (
    <div className="card p-4 flex flex-col items-center gap-3">
      <Skeleton className="w-12 h-12 rounded-lg" />
      <Skeleton className="h-3 w-20" />
    </div>
  );
}

/** Hero card skeleton for league / team pages. */
export function HeroSkeleton() {
  return (
    <div className="card p-6 sm:p-8 mb-8">
      <div className="flex items-center gap-5">
        <Skeleton className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl shrink-0" />
        <div className="space-y-3 flex-1">
          <Skeleton className="h-6 w-56 max-w-full" />
          <Skeleton className="h-3 w-40 max-w-full" />
        </div>
      </div>
    </div>
  );
}

/** Match hero skeleton with two teams and VS. */
export function MatchHeroSkeleton() {
  return (
    <div className="card p-6 sm:p-8 mb-8">
      <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
        <div className="flex flex-col items-center gap-2 flex-1">
          <Skeleton className="w-16 h-16 sm:w-20 sm:h-20 rounded-full" />
          <Skeleton className="h-4 w-28" />
        </div>
        <Skeleton className="h-8 w-8 rounded-full" />
        <div className="flex flex-col items-center gap-2 flex-1">
          <Skeleton className="w-16 h-16 sm:w-20 sm:h-20 rounded-full" />
          <Skeleton className="h-4 w-28" />
        </div>
      </div>
      <div className="flex items-center justify-center gap-4 mt-6">
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-3 w-24" />
      </div>
    </div>
  );
}

/** Comparison group skeleton. */
export function ComparisonGroupSkeleton() {
  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-700/50">
        <Skeleton className="h-4 w-40" />
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex flex-col items-center p-4 rounded-lg border border-slate-700/40 space-y-3">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-7 w-20" />
            <Skeleton className="h-2 w-32" />
            <Skeleton className="h-3 w-12" />
          </div>
        ))}
      </div>
    </div>
  );
}
